import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { type IAlertProps } from 'shared/ui/Alert/types'
import { GetNumberModalActionsDefault } from 'entities/Phone/ui/GetNumberModal/ui/GetNumberModalDefault/GetNumberModalActionsDefault'
import { GetNumberModalContentDefault } from 'entities/Phone/ui/GetNumberModal/ui/GetNumberModalDefault/GetNumberModalContentDefault'
import { NewNumberSummaryModalContent } from 'entities/Phone/ui/GetNumberModal/ui/NewNumberSummaryModal/NewNumberSummaryModalContent'
import { NewNumberSummaryModalActions } from 'entities/Phone/ui/GetNumberModal/ui/NewNumberSummaryModal/NewNumberSummaryModalActions'
import { PhoneApi } from 'entities/Phone'
import { GetNumberModalActions } from 'entities/Phone/ui/GetNumberModal/ui/GetNumberModal/GetNumberModalActions'
import { GetNumberModalContent } from 'entities/Phone/ui/GetNumberModal/ui/GetNumberModal/GetNumberModalContent'
import { type IGetNumberModalStoreAddNumber } from 'entities/Phone/ui/GetNumberModal/store/types'
import { ITabsEnum } from 'entities/Phone/types'
import { ChooseNumberTabsStore } from 'entities/Phone/ui/ChooseNumberTabs/store/ChooseNumberTabsStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class GetNumberModalStore {
  private _idModal = 'getNumberModal'
  private _idModalFinish = 'getNumberFinishModal'
  private _loading = false
  private _loadingNumber = false
  private _showFirstRegAlert = true
  private _addNumberError: string | null = null

  private _chooseNumberStore: ChooseNumberTabsStore

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._chooseNumberStore = new ChooseNumberTabsStore(this._entities)
  }

  get chooseNumberStore() {
    return this._chooseNumberStore
  }

  get loadingNumber() {
    return this._loadingNumber
  }

  get disabled() {
    return Boolean(!this.chooseNumberStore.selectedNumber || this._loading || this._loadingNumber)
  }

  get loading() {
    return this._loading
  }

  get showFirstRegAlert() {
    return this._showFirstRegAlert && !this._entities.organizationStore.isTwilioNumberVendor
  }

  get addNumberError() {
    return this._addNumberError
  }

  clearAddNumberError = () => {
    this._addNumberError = null
  }

  get addNumberAlertProps(): IAlertProps | null {
    if (!this._addNumberError) return null
    return {
      marginBottom: 24,
      item: {
        type: 'error',
        desc: this._addNumberError,
      },
      onClose: this.clearAddNumberError,
    }
  }

  get numberType() {
    return this._entities.complianceStore.summary?.numbers_type
  }

  get showOnboardingMode() {
    return (
      this._entities.usersStore.user?.isRequireNumber &&
      !this._entities.subscriptionStore.isAgencyWithoutSubscription
    )
  }

  editAnswersHandler = () => {
    this.closeModal()
  }

  handleAddNumberForChooseNumber = () => {
    const isRedirect =
      !this._entities.usersStore.user?.isMemberRole &&
      !this._entities.usersStore.user?.isManagerRole

    this.handleAddNumber({ isRedirect })
  }

  openDefault = async () => {
    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.subscriptionStore.initPromise,
      this._entities.organizationStore.initPromise,
    ])

    if (this._entities.organizationStore.isSinchNumberVendor) {
      this.chooseNumberStore.setActiveTab(ITabsEnum.LOCAL)
    }

    if (
      this._entities.usersStore.user?.isRequireNumber &&
      !this._entities.subscriptionStore.isAgencyWithoutSubscription &&
      !this._entities.organizationStore.isTwilioNumberVendor &&
      !this._entities.usersStore.user.inboxesCount
    ) {
      modalStore.addModal({
        id: this._idModal,
        showCloseIcon: false,
        showCloseButton: false,
        disabledOnAllClose: true,
        disabledOnEscClose: true,
        disabledOverlayClose: true,
        title: 'Choose number',
        paddingContent: '8px 24px 0 24px',
        width: 480,
        ModalContentProps: {
          store: this,
        },
        ModalActions: GetNumberModalActionsDefault,
        ModalContent: GetNumberModalContentDefault,
        onClose: this.closeModal,
      })
    }
  }

  openOnboarding = async () => {
    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.subscriptionStore.initPromise,
      this._entities.organizationStore.initPromise,
      this._entities.complianceStore.fetchSummary(),
    ])

    if (this.showOnboardingMode) {
      this.checkFirstOpen()
      this.chooseNumberStore.preselectCountry()
      this.chooseNumberStore.initActiveTab()
      this.chooseNumberStore.setUseUnavailableContent(true)

      modalStore.addModal({
        id: this._idModal,
        showCloseIcon: false,
        showCloseButton: false,
        disabledOnAllClose: true,
        disabledOnEscClose: true,
        disabledOverlayClose: true,
        hiddenContent: true,
        paddingContent: '8px 24px 0 24px',
        width: 480,
        ModalContentProps: {
          store: this,
        },
        showHeader: false,
        ModalActions: GetNumberModalActions,
        ModalContent: () => (
          <GetNumberModalContent
            store={this.chooseNumberStore}
            showAlert={this.showFirstRegAlert}
            editAnswersHandler={this.editAnswersHandler}
            onClose={this.closeModal}
          />
        ),
        onClose: () => {
          this.closeModal()
        },
      })
    }
  }

  openNewNumberSummaryModal = (number: string) => {
    modalStore.addModal({
      id: this._idModalFinish,
      showCloseIcon: false,
      showCloseButton: false,
      disabledOnAllClose: true,
      disabledOnEscClose: true,
      disabledOverlayClose: true,
      paddingContent: '40px 24px 16px 24px',
      width: 480,
      showHeader: false,
      ModalContentProps: {
        store: this,
        number: number,
      },
      ModalActions: NewNumberSummaryModalActions,
      ModalContent: NewNumberSummaryModalContent,
      onClose: this.closeFinishModal,
    })
  }

  closeFinishModal = () => {
    modalStore.removeModal(this._idModalFinish)
  }

  closeModal = () => {
    this.setFirstOpen()
    this.dispose()
    modalStore.removeModal(this._idModal)
  }

  checkFirstOpen = () => {
    const value = Boolean(
      localStorage.getItem(`${this._idModal}_alert_open_${this._entities.usersStore.user_owner_id}`)
    )

    this._showFirstRegAlert = !value
  }

  setFirstOpen = () => {
    localStorage.setItem(
      `${this._idModal}_alert_open_${this._entities.usersStore.user_owner_id}`,
      '1'
    )
  }

  dispose = () => {
    this.chooseNumberStore.setNumber(null)
    this._loading = false
    this._addNumberError = null
    this.chooseNumberStore.setActiveTab(ITabsEnum.TOLL_FREE)
  }

  handleAddNumber = async ({ isRedirect = true }: IGetNumberModalStoreAddNumber = {}) => {
    if (!this.chooseNumberStore.selectedNumber) return

    try {
      runInAction(() => {
        this._loading = true
        this._loadingNumber = true
        this._addNumberError = null
      })

      const { data } = await PhoneApi.addNumber({
        number: this.chooseNumberStore.selectedNumber?.number ?? '',
      })

      this._entities.numbersStore.addItem(data)

      await window.publicApiVue?.fetchMe()
      await window.publicApiVue?.fetchNumbers()
      await Promise.all([
        this._entities.usersStore.fetchUsersMe(),
        this._entities.numbersStore.fetchOrganizationNumber(),
      ])
      await this._entities.complianceStore.loadAndUpdateUnverifiedTollFreeNumbers()

      if (isRedirect) {
        uiStore.changeRouteViaVue({
          path: '/settings/organization/compliance',
        })
      }

      this.openNewNumberSummaryModal(data.formatted_number)
      this.closeModal()
    } catch (e) {
      if (e instanceof AxiosError) {
        runInAction(() => {
          this._addNumberError =
            e.errors.getError('message') || 'Something went wrong. Please try again.'
        })
      }
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
        this._loadingNumber = false
      })
    }
  }
}
